import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from "@angular/router";
import {RoleChangeListener, StartupService} from "../../service-ui/startup.service";
import {Cart, UnifiedShippingChoice} from "../../../model/cart/Cart";
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {LoggedInCallback} from "../../service-auth/cognito.service";
import {ProductService} from "../../service-data/product.service";
import {Order} from "../../../model/Order";
import {OrderService} from "../../service-data/order.service";
import {SimpleOrder} from "../csv/csv-page/SimpleOrder";
import {OrderDeliverTo} from "../../../model/OrderDeliverTo";
import {OrderItem} from "../../../model/OrderItem";
import {OrderDeliverToService} from "../../service-data/order-deliver-to.service";
import {PrintNodeOrderService} from "../../service-data/print-node-order.service";
import {SimpleItem} from "../csv/csv-page/SimpleItem";
import {ValidationAddress} from "../../service-data/address-validation.service";
import {takeUntil} from "rxjs/operators";
import {DraftService} from "../../service-data/draft.service";
import {DraftBodyService} from "../../service-data/draft-body.service";
import {DraftCoverService} from "../../service-data/draft-cover.service";
import {BookService} from "../../service-data/book.service";
import {ProjectService} from "../../service-data/project.service";
import {CreateProjectLauncherService} from "../../service-ui/create-project-launcher.service";
import {BodyService} from "../../service-data/body.service";
import {CoverService} from "../../service-data/cover.service";
import {Subject} from "rxjs";
import {AddressService} from "../../service-data/address-service";
import {Address} from "../../../model/Address";
import {ChargeProfileService} from "../../service-data/charge-profile-service";
import {SalesTaxService} from "../../service-data/sales-tax.service";
import {AlertService} from "../../service-ui/alert.service";
import {LanguagePipe} from "../../pipe/language.pipe";
import {MatSnackBar} from "@angular/material/snack-bar";
import {environment} from "../../../environments/environment";
import {describeServerError} from "../../service-data/http-error";

@Component({
  selector: 'app-cart-page',
  templateUrl: './cart-page.component.html',
  styleUrls: ['./cart-page.component.css', './cart-items.css']
})
export class CartPageComponent implements OnDestroy, OnInit, OnChanges, LoggedInCallback, RoleChangeListener {
  @Input() narrow: boolean;
  @Input() searchString: string = '';
  @Input() showDetails: boolean = true;
  @Input() showCoverImage: boolean = true;
  @Input() showOnlyRecent: boolean = false;
  @Input() showNonZero: boolean = true;
  @Input() showProducts: boolean = true;
  @Input() showDrafts: boolean = true;
  @Input() showTable: boolean = true;
  @Input() mode: string = 'CART';
  @Output() modeChange = new EventEmitter<string>();

  protected readonly Number = Number;
  // `countries` and `usStates` were moved to the extracted address /
  // payment section components; the parent no longer renders dropdowns
  // directly. `COUNTRIES` and `US_STATES` imports are no longer needed
  // here either.
  //
  // The legacy `addressMode` / `shippingMode` / `paymentMode` fields
  // were assigned but never read; dropped during the section-extraction
  // refactor (2026-06-24).

  invoice: boolean = false;

  addresses: Address[] = [];

  onDestroySubject: Subject<boolean> = new Subject();
  state: string = 'ITEMS';
  itemsReady: boolean = false;
  addressEditable: boolean = false;
  addressReady: boolean = false;
  shippingEditable: boolean = false;
  shippingReady: boolean = false;
  /**
   * True when a valid shipping choice is committed to the cart —
   * derived DIRECTLY from cart state on every change-detection tick
   * rather than tracked as a separate field. Kept decoupled from
   * {@link shippingReady} so the Place Order button can enable the
   * moment auto-select lands a default choice (address + payment
   * permitting) WITHOUT causing the shipping accordion to auto-
   * collapse (that's driven by `shippingReady`, which stays false
   * until the user actively picks a row so they still see the
   * picker after an org-default auto-selection).
   *
   * Using a getter (instead of a manually-set field) closes the
   * race window where auto-select took a code path that didn't
   * re-emit `shippingChoiceAutoSelected` (e.g. the "existing
   * selection, costs unchanged" branch in
   * `CartShippingSectionComponent.maybeAutoSelectChoice`) and the
   * flag drifted false while the cart actually held a valid
   * selection. Cart state is the source of truth here — the getter
   * simply reflects it.
   */
  get shippingSelectionMade(): boolean {
    return !!this.cart
        && this.cart.selectedOrderOption != null
        && !!this.cart.selectedCarrier
        && !!this.cart.selectedServiceLevel;
  }
  paymentEditable: boolean = false;
  paymentReady: boolean = false;
  userControlledAccordion: boolean = false;

  editDeliveryAddress: boolean = false;
  enterCreditCard: boolean = false;

  deliveryAddressChanged: boolean = false;

  cart: Cart;
  // products: Product[] = [];
  addressIsComplete: boolean = false;
  shippingVerified: boolean = false;
  paymentVerified: boolean = false;
  costsAndShippingVerified: boolean = false;
  billingAddressTheSameAsShipping: boolean = true;
  savePaymentInfoAsChargeProfile: boolean = false;
  savePaymentInfoAsChargeProfileName: string = '';
  expeditedManufacturing: boolean = false;
  placingOrderMessage: string = '';
  placingOrderStatus: string = '';
  createdOrderDeliverTo: OrderDeliverTo;
  validatedAddress: ValidationAddress;

  totalQuantity: number = 0;
  totalWeight: number = 0;
  totalCost: number = 0;

  /**
   * Controls the collapsible non-zero-items panel inside the cart
   * summary bar. When true, the summary bar expands to show a compact
   * read-only list of the items currently in the cart (id, title,
   * qty, cost, weight) — same shape as the Order Summary shown at the
   * top of the checkout stage, but non-editable and integrated with
   * the existing summary totals. Collapsed by default so the summary
   * bar keeps its original footprint.
   */
  summaryExpanded: boolean = false;

  firstFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  thirdFormGroup: FormGroup;

  messages: {
    validDate: 'GOOD\nTHRU',
    monthYear: 'mm/aaaa'
  };
  placeholders: {
    number: '•••• •••• •••• ••••',
    name: 'Nome Completo',
    expiry: '••/••',
    cvc: '•••'
  };
  masks: {
    cardNumber: '•' // optional - mask card number
  };


  facilityFriendlyNames = {
    '1': 'Phoenix, AZ',
    '7': 'Houston, Tx'
  };
  constructor(public router: Router, public startupService: StartupService, private productService: ProductService,
              private orderService: OrderService, private orderDeliverToService: OrderDeliverToService,
              private printNodeOrderService: PrintNodeOrderService, private draftService: DraftService,
              private draftBodyService: DraftBodyService, private draftCoverService: DraftCoverService,
              private bookService: BookService, private bodyService: BodyService, private coverService: CoverService,
              private addressService: AddressService,
              protected chargeProfileService: ChargeProfileService, private formBuilder: FormBuilder,
              private salesTaxService: SalesTaxService, private alertService: AlertService,
              private snackBar: MatSnackBar, private projectService: ProjectService,
              private createProjectLauncherService: CreateProjectLauncherService) {
    this.startupService.addRoleChangeListener(this);
  }

  // ── Empty cart ────────────────────────────────────────────────────────
  /**
   * True when this Organization owns no Books, no Drafts and no
   * Projects — i.e. an empty cart here is not "you haven't picked
   * anything yet", it is "you have nothing to pick from".
   *
   * Only used to decide how much explanation the empty state
   * offers. The Create Project button is shown either way, because
   * creating a Project is a reasonable next step even for an
   * account that already has content.
   */
  get hasNothingToOrder(): boolean {
    const books = (this.bookService && this.bookService.objects) || [];
    const drafts = (this.draftService && this.draftService.objects) || [];
    const projects = (this.projectService && this.projectService.objects) || [];
    return books.length === 0 && drafts.length === 0 && projects.length === 0;
  }

  /**
   * Empty-cart call to action.
   *
   * Replaces a `[routerLink]="['/secure/books']"` button labelled
   * "Add books to your library". That route has never been
   * registered (see `app-routing.module.ts`), so the button did
   * nothing at all — and there is no "library" concept in this app
   * either; content is held in Projects.
   *
   * The launcher opens the same Create Project dialog the Projects
   * screen uses and then routes there, so this page does not need
   * to know anything about Projects, Drafts or their services.
   */
  createProject(): void {
    this.createProjectLauncherService.createProjectAndOpenDraft('/');
  }

  // ── Toasts ────────────────────────────────────────────────────────────
  // Prefer Material snack-bars over the legacy stacked-banner AlertService
  // for user-facing place-order feedback. AlertService remains injected
  // for any code path that still uses it, but new call sites should route
  // through these helpers so the notification is unobtrusive and
  // auto-dismisses (same pattern already adopted by
  // RateConfigEditorComponent — see the "Toasts" block in that file).
  private toastSuccess(message: string): void {
    this.snackBar.open(message, LanguagePipe.get('Dismiss', 'compressSpaces'),
      {duration: 4000, panelClass: ['cart-snack-success']});
  }
  private toastError(message: string): void {
    this.snackBar.open(message, LanguagePipe.get('Dismiss', 'compressSpaces'),
      {duration: 6000, panelClass: ['cart-snack-error']});
  }


  ngOnInit() {
    console.log("AppComponent: Checking if the user is already authenticated");
    this.startupService.isAuthenticated(this);
    this.firstFormGroup = this.formBuilder.group({
      firstCtrl: ['', Validators.required]
    });
    this.secondFormGroup = this.formBuilder.group({
      secondCtrl: ['', Validators.required]
    });
    this.thirdFormGroup = this.formBuilder.group({
      thirdCtrl: ['', Validators.required]
    });
    this.totalCost = 0;
    this.totalWeight = 0;
    this.totalQuantity = 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['mode'] && this.cart) {
      const prev = changes['mode'].previousValue;
      const curr = changes['mode'].currentValue;
      if (curr === 'CHECKOUT') {
        this.proceedToCheckout();
      } else if (prev === 'CHECKOUT' && curr === 'CART') {
        // User clicked "Back to Cart" (see `CartComponent.backToCart`).
        // Invalidate the cached shipping options right away so the
        // downstream state doesn't linger stale into a fresh CHECKOUT
        // pass — any quantity/item edits made while in CART mode will
        // now transparently trigger a fresh compute the next time the
        // user proceeds to CHECKOUT, and the shipping section won't
        // flash the previous cart's rates while the new compute is
        // in flight (bug reported 2026-07-09: "shipping options from
        // the earlier cart quantities are still presented").
        this.cart.invalidateDownstream();
        this.shippingReady = false;
      }
    }
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    } else {
      this.cart = this.startupService.getCart();
      this.getData();
      this.verifyCostsAndShipping();
      this.verifyPaymentInfo();
      this.checkAddressIsComplete();
    }
  }
  roleChanged(role: string, roleId: string): void {
    this.getData();
  }

  getData() {
    this.cart = this.startupService.getCart();
    // Payee / Inventory Type are ServiceProvider- and Network-only
    // choices; the checkout hides both selectors for everyone else. Snap
    // the cart back to the Organization defaults here — at the point the
    // cart is (re)bound to the signed-in user / role — so a cart carried
    // over from a previous session or a different role can't smuggle a
    // payer / inventory type the current user can neither see nor change
    // into the place-order envelope.
    if (this.cart && !this.startupService.canChooseFulfillmentOptions()) {
      if (this.cart.applyDefaultFulfillmentOptions()) {
        console.log('CartPageComponent: reset cart payer / inventory type to the Organization defaults');
      }
    }
    // Bug 2026-07-22: when the shipping picker auto-selects the
    // organization's default carrier/service, `shippingChoiceAutoSelected`
    // fires the instant the first quote lands — but per-package rate
    // calls are still in flight. `maybeComputeSalesTax` correctly
    // refuses to compute against the partial tree, and previously
    // nothing later re-invoked it, so sales tax never ran and the
    // order never advanced to "ready to place". Subscribing to the
    // cart's `shippingFullyComputedNotifier` gives us a reliable
    // "now the tree is complete" signal that re-triggers the tax
    // pass regardless of whether the shipping choice was auto- or
    // manually selected.
    if (this.cart && this.cart.shippingFullyComputedNotifier) {
      this.cart.shippingFullyComputedNotifier
        .pipe(takeUntil(this.onDestroySubject))
        .subscribe(() => {
          this.maybeComputeSalesTax();
        });
    }
    // Paired notifier: fires whenever `cart.setSelectedShippingChoice`
    // runs (both manual pick and auto-select microtask). Between the
    // two subscriptions the tax pass is guaranteed to fire once BOTH
    // prerequisites are true — "a choice is selected" and "the
    // selected layout's per-package rates have landed" — no matter
    // which order they settle in.
    if (this.cart && this.cart.selectedShippingChoiceNotifier) {
      this.cart.selectedShippingChoiceNotifier
        .pipe(takeUntil(this.onDestroySubject))
        .subscribe(() => {
          this.maybeComputeSalesTax();
        });
    }
    // Bug 2026-07-22 (pass 2): when the user deletes a saved payment
    // method that is currently selected on the cart, the deletion
    // wasn't propagated to `cart.chargeProfile`. The removed profile
    // stayed painted as "selected" in the payment section and
    // `Cart.isPaymentInfoReady()` kept returning true (still holding
    // the dangling reference), so the Place-Order button remained
    // enabled against a payment method that no longer exists
    // server-side. Subscribe to the service's delete notifier and:
    //   1. Clear `cart.chargeProfile` when the deleted profile is
    //      the currently-selected one.
    //   2. Fall back to another `default: true` profile if one
    //      remains in the address book, otherwise leave the cart
    //      unselected so the user is forced to pick / enter a card.
    //   3. Re-run `verifyPaymentInfo()` so `paymentReady` /
    //      `paymentVerified` refresh and the Place-Order button
    //      disables until a valid selection returns.
    this.chargeProfileService.getDeleteNotifier()
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe((deleted: any) => {
        if (!this.cart || !deleted) { return; }
        const deletedId = (typeof deleted.getId === 'function') ? deleted.getId() : deleted.ID;
        if (this.cart.chargeProfile && this.cart.chargeProfile.ID === deletedId) {
          // Try to fall back to any remaining default profile.
          const fallback = (this.chargeProfileService.objects || [])
            .find((p: any) => p && p.default && p.ID !== deletedId);
          this.cart.setChargeProfile(fallback ? fallback : undefined);
        }
        this.verifyPaymentInfo();
      });
    this.draftService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.cart.addDraftItem(o, 0);
      this.retryDraftRatesIfPreviouslyErrored(o);
      this.verifyCostsAndShipping();
    });
    this.draftService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.cart.addDraftItem(o, 0);
      // A Draft's linked `draftBody` / `draftCover` arrive AFTER the
      // Draft itself in the general case (DraftService.getLinkData
      // wires them via async lookups). If the Draft was already in
      // the cart when the initial rate-fetch fired with an incomplete
      // payload (empty body / cover), the rate ended up cached as
      // ERROR. Retry now so the completed Draft gets a real rate
      // without the user having to click the per-line Retry button.
      this.retryDraftRatesIfPreviouslyErrored(o);
      this.verifyCostsAndShipping();
    });
    // DraftBody / DraftCover (used by DraftItem) and Book / Body /
    // Cover (used by ProductItem) all arrive on their own services
    // separately from the parent Draft / Product. When they land
    // they attach to the parent via NotificationsService (see
    // AbstractLiveDataService.linkObject → resolveObjectAttachmentRequets),
    // but that attachment does NOT emit a parent update notification,
    // so the draft-/product-service listeners above never fire from
    // it. Subscribe here so we can (a) retry the deferred rate fetch
    // for any DraftItem / ProductItem whose inputs just completed,
    // and (b) re-fire the shipping-package computation which was
    // similarly deferred in `PrintNodeOrderOption.compute()`.
    this.draftBodyService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.draftBodyService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.draftCoverService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.draftCoverService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.bookService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.bookService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.bodyService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.bodyService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.coverService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    this.coverService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe(() => {
      this.onLinkedResourceArrived();
    });
    // this.draftService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
    //   this.cart.addDraftItem(o, 0);
    // });
    this.draftService.objects.forEach( o => {
      this.cart.addDraftItem(o, 0);
    });

    this.productService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      if (o.productType === 'BOOK') {
        this.cart.addProductItem(o, 0);
        this.verifyCostsAndShipping();
      }
    });
    this.productService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      if (o.productType === 'BOOK') {
        this.cart.addProductItem(o, 0);
        this.verifyCostsAndShipping();
      }
    });
    // this.productService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
    // });
    this.productService.objects.forEach( o => {
      if (o.productType === 'BOOK') {
        this.cart.addProductItem(o, 0);
      }
    });
    this.cart.items.sort(( a, b): number => {
      if ( (a.tagId - 1) % 24 > (b.tagId - 1) % 24 ) {
        return 1;
      } else if ( (a.tagId - 1) % 24 === (b.tagId - 1) % 24 ) {
        if ( a.tagId > b.tagId ) {
          return 1;
        }
        if ( a.tagId < b.tagId ) {
          return -1;
        }
        return 0;
      } else {
        return -1;
      }
    });
    this.chargeProfileService.objects.forEach( o => {
      if ( o.default ) {
        if ( !this.cart.chargeProfile || this.cart.chargeProfile.ID === 0 ) {
          this.cart.setChargeProfile(o);
        }
      }
    });
    this.addresses = [];
    this.addressService.objects.forEach( o => {
      this.addresses.push(o);
      if ( o.default ) {
        if ( !this.cart.address || this.cart.address.ID === 0 ) {
          this.cart.setAddress(o);
        }
      }
    });
    this.verifyCostsAndShipping();
    // Kick off production-rate fetch for any non-zero item already in
    // the cart. Without this, items that were added in a previous
    // session display $0.00 in CART mode until the user either nudges
    // a quantity or proceeds to CHECKOUT (which also triggers the
    // fetch). Idempotent — items already READY or LOADING are skipped.
    this.ensureProductionRatesForNonZeroItems();
    this.logCartContentsForDev();
  }

  /**
   * One-time dev/QA console summary of the cart's contents — id, qty,
   * weight (raw + per-line), cached cost, production-rate status. Helps
   * answer "are weights / prices coming through correctly?" without
   * having to be logged in as an internal `@snowfallpress.com` operator
   * (which is what gates the diagnostic-button strip). Suppressed in
   * production builds.
   */
  private logCartContentsForDev() {
    if (environment.production) { return; }
    if (!this.cart || !this.cart.items) { return; }
    const rows = this.cart.items.map(i => {
      const base: any = i.getBase ? i.getBase() : null;
      const isProduct = this.isInstanceOf(i, 'ProductItem');
      const bookWeight = isProduct && base && base.book ? base.book.weight : null;
      const productWeight = isProduct && base ? base.productWeight : null;
      return {
        type: isProduct ? 'Product' : 'Draft',
        id: i.getId ? i.getId() : null,
        title: i.getTitle ? i.getTitle() : '',
        quantity: i.quantity,
        unitWeight: i.getWeight ? i.getWeight() : null,
        lineWeight: i.getWeight && i.quantity > 0 ? i.getWeight() * i.quantity : 0,
        bookWeight, productWeight,
        cachedCost: i.cost,
        productionRatesStatus: (i as any).productionRatesStatus,
        costFacilityId: (i as any).costFacilityId,
      };
    });
    console.log('[cart] contents snapshot (dev-only). If unitWeight is 0 the server-side Book.weight is empty; if productionRatesStatus is not READY the cached cost will be 0 until the rates fetch completes.', rows);
  }

  proceedToCheckout() {
    if ( this.totalQuantity > 0 ) {
      this.cart.cartStage = 'CHECKOUT';
      this.modeChange.emit('CHECKOUT');
      this.itemsReady = true;
      this.addressEditable = true;
      this.userControlledAccordion = false;
      this.cart.setNonZeroItems();
      this.checkAddressIsComplete();
      this.shippingReady = this.cart.selectedOrderOption != null;
      // Once we are in CHECKOUT, ensure rates are loaded for every non-zero item
      // (idempotent — already-loaded items return cached rates immediately).
      this.ensureProductionRatesForNonZeroItems();
      this.maybeComputeSalesTax();
      // Auto-trigger the legacy shipping computation the moment we
      // land in CHECKOUT if the address is already ready (which is
      // the common case — the user has a default validated address
      // from their address book, or they just came back from a
      // previous CHECKOUT pass with the address still selected).
      // `maybeAutoComputeShipping` is idempotent — if fresh rates are
      // already cached for the current item + address combination
      // it does nothing. Otherwise it fires `cart.compute()` so the
      // user doesn't have to click "Compute order costs" first
      // (bug reported 2026-07-09: shipping options weren't computed
      // automatically when re-entering checkout).
      this.maybeAutoComputeShipping();
    } else {
      this.cart.cartStage = 'CART';
      this.modeChange.emit('CART');
      this.itemsReady = false;
      this.addressEditable = false;
    }
  }

  /**
   * Sync the parent's `addresses` array when the address-section child
   * mutates it (add via save, remove via soft-delete). The child owns
   * the address-book CRUD operations against `AddressService`; the
   * parent only holds the array for use elsewhere (e.g. initial-data
   * load in `getData()`).
   */
  onAddressesChange(addresses: Address[]) {
    this.addresses = addresses;
  }

  /**
   * Called when the shipping section reports the user has picked a
   * carrier + service from the unified list. The child component has
   * already applied the choice to `cart` via
   * {@link Cart.setSelectedShippingChoice}; the parent's remaining
   * job is to flip `shippingReady` (so the payment section becomes
   * eligible and the summary bar refreshes) and to trigger a sales-
   * tax recompute if the tax rate depends on the shipping cost.
   */
  onShippingChoicePicked(_choice: UnifiedShippingChoice) {
    if (!this.cart) { return; }
    this.shippingReady = this.cart.selectedOrderOption != null
        && !!this.cart.selectedCarrier
        && !!this.cart.selectedServiceLevel;
    // `shippingSelectionMade` (the Place-Order button gate) is a
    // getter derived from cart state — no manual set needed here.
    this.maybeComputeSalesTax();
  }

  /**
   * Handler for the shipping-section's auto-select event. Fires when
   * the picker seeds a default choice (organization preference or
   * cheapest) without user interaction. Recompute sales tax + refresh
   * the order-summary totals so the pre-selected default actually
   * feeds the cart totals — otherwise the user sees "Organization
   * default" marked as selected but the cart total ignores its
   * shipping/packing cost until they manually pick a different row
   * and pick back (bug 2026-07-20).
   *
   * Deliberately does NOT flip `shippingReady`. That flag drives the
   * accordion auto-collapse; keeping it false lets the user still see
   * the picker after auto-select. `shippingReady` only flips when the
   * user actively clicks a choice via `onShippingChoicePicked`.
   *
   * The Place-Order button gate uses {@link shippingSelectionMade}
   * instead — a getter derived directly from cart state, so the
   * button enables the moment `cart.setSelectedShippingChoice` runs
   * (whether from this handler's upstream code path or from any
   * other) without needing this handler to set a separate flag.
   */
  onShippingChoiceAutoSelected(_choice: UnifiedShippingChoice) {
    if (!this.cart) { return; }
    this.maybeComputeSalesTax();
  }

  checkAddressIsComplete() {
    if ( this.billingAddressTheSameAsShipping ) {
      this.cart.setBillingAddressTheSameAsShipping();
    }
    this.addressIsComplete = this.cart.isAddressReady();
    this.addressReady = this.addressIsComplete;
    if ( !this.addressReady ) {
      this.shippingReady = false;
    }
    this.paymentVerified = this.cart.isPaymentInfoReady();
    this.paymentReady = this.paymentVerified || this.invoice;
    // When the new cart shipping flow is enabled and the address just
    // became ready, kick off the new getFacilities → rateShipping pipeline
    // alongside (not instead of) the legacy `Cart.compute()` flow. The new
    // flow drives `cart.facilityCandidates` / `cart.rateShippingByFacility`
    // for downstream consumers and the diagnostic test buttons; the legacy
    // flow continues to drive the user-visible shipping radio group until
    // the consumer UI is rewritten in a follow-up PR.
    this.maybeRunNewShippingFlow();
    // Also kick off the legacy shipping computation the moment the
    // address settles (validated / manually-overridden / a saved
    // address was picked). Historically the only paths that fired
    // `cart.compute()` were `Cart.setAddress()` (which handles the
    // saved-address radio click and the initial default-address load)
    // and the manual "Compute order costs" button. The
    // `applyValidatedAddress()` flow — accepting a SmartyStreets
    // suggestion — never triggered a compute, and neither did the
    // `overrideAddressValidation()` flow, so users had to click the
    // Compute button before any shipping options appeared. Fire the
    // idempotent auto-compute here so any code path that ends with
    // "the address is now ready" gets fresh shipping options for
    // free (bug reported 2026-07-09).
    this.maybeAutoComputeShipping();
  }

  // verifyShipping() {
  //   this.addressIsComplete = this.cart.isShippingReady();
  // }

  verifyPaymentInfo() {
    if ( this.billingAddressTheSameAsShipping ) {
      this.cart.setBillingAddressTheSameAsShipping();
    }
    this.paymentVerified = this.cart.isPaymentInfoReady();
    this.paymentReady = this.paymentVerified || this.invoice;
    this.checkAddressIsComplete();
  }

  verifyCostsAndShipping () {
    this.totalQuantity = 0;
    this.totalWeight = 0;
    this.totalCost = 0;
    for ( const item of this.cart.items ) {
      if ( item.quantity > 0 ) {
        this.totalQuantity++;
        try {
          this.totalCost += item.getCost();
          this.totalWeight += ( item.getWeight() * item.quantity );
        } catch (e) {
          console.error('Error computing cost/weight for item', item, e);
        }
      }
    }
    this.cart.itemsStatus = this.totalQuantity > 0 ? 'READY' : 'EMPTY';
    if ( this.totalQuantity === 0 ) {
      this.cart.cartStage = 'CART';
      this.modeChange.emit('CART');
    } else {
      // Re-attempt sales tax computation in case shipping is already READY.
      this.maybeComputeSalesTax();
    }
  }

  /**
   * Ensure every non-zero cart item has its ProductionRateTable loaded.
   * Cached entries return immediately; missing entries trigger a server fetch
   * and refresh the item's cost when the rates arrive.
   */
  private ensureProductionRatesForNonZeroItems() {
    if (!this.cart || !this.cart.items) { return; }
    for (const item of this.cart.items) {
      if (item.quantity > 0 && item.productionRatesStatus !== 'READY' && item.productionRatesStatus !== 'LOADING') {
        this.fetchProductionRatesForItem(item);
      }
    }
  }

  private fetchProductionRatesForItem(item: any) {
    this.orderService.getProductionRatesForItem(item).subscribe(() => {
      try { item.computeCost(); } catch (e) { console.error('computeCost after rates load failed', e); }
      this.verifyCostsAndShipping();
      if (!environment.production) {
        console.log(`[cart] production rates ready for item ${item.getId ? item.getId() : '?'} "${item.getTitle ? item.getTitle() : ''}": status=${item.productionRatesStatus}, cachedCost=$${(item.cost || 0).toFixed(2)}, costFacilityId=${item.costFacilityId}`);
      }
    });
  }

  /**
   * Manual retry hook wired to the "Retry" button that appears on a
   * per-line price block when the item's pricing state is `ERROR`.
   * Clears the failed cache entry so the fetch actually re-hits the
   * server (`OrderService.getProductionRatesForItem` returns the cached
   * empty table otherwise) and re-fires the request with the same
   * accounting the initial fetch used.
   */
  retryItemPricing(item: any): void {
    if (!item) { return; }
    // Clear the per-item cache so the next call is a real HTTP hit
    // rather than a cache-hit that resurrects the previous empty table.
    try {
      if (item.getId) { this.orderService.clearProductionRateCache(item.getId()); }
    } catch (e) { console.error('clearProductionRateCache failed for retry', e); }
    item.productionRatesStatus = 'NONE';
    this.fetchProductionRatesForItem(item);
  }

  /**
   * When a Draft update notification arrives (typically after its
   * linked `draftBody` / `draftCover` finally load), find the
   * corresponding cart line and re-fetch its rate table IF the
   * initial fetch either failed (`ERROR`) or was deferred (`NONE`
   * — see `OrderService.getProductionRatesForItem`, which now short-
   * circuits when `hasCompleteRateInputs()` returns false to avoid
   * sending a payload with zero-valued dimensions). No-op when the
   * line isn't in the cart, isn't held at a positive quantity, is
   * already `READY`/`LOADING`, or its inputs are still incomplete.
   */
  private retryDraftRatesIfPreviouslyErrored(draft: any): void {
    if (!this.cart || !this.cart.items || !draft || !draft.getId) { return; }
    const draftId = draft.getId();
    for (const item of this.cart.items) {
      if (!this.isInstanceOf(item, 'DraftItem')) { continue; }
      if (item.getId && item.getId() === draftId
          && item.quantity > 0
          && (item.productionRatesStatus === 'ERROR' || item.productionRatesStatus === 'NONE')
          && (typeof (item as any).hasCompleteRateInputs !== 'function' || (item as any).hasCompleteRateInputs())) {
        console.log(`[cart] draft ${draftId} inputs ready — (re)firing rate fetch (was ${item.productionRatesStatus})`);
        this.retryItemPricing(item);
      }
    }
  }

  /**
   * Fires whenever a `Book` / `Body` / `Cover` / `DraftBody` /
   * `DraftCover` row lands on its own service — these are the
   * asynchronously-linked children of `Product` and `Draft` that
   * feed the rate + shipping payloads. When their parent gains
   * complete inputs we (a) re-fire any per-item deferred /
   * errored production-rate fetch, and (b) re-run
   * `Cart.compute()` so the shipping-package call that
   * `PrintNodeOrderOption.compute()` previously deferred (because
   * `hasCompleteRateInputs()` was false) now fires with the real
   * geometry. All work is idempotent — items already `READY` are
   * skipped, and `Cart.compute()` no-ops when the cart has no
   * address / no non-zero items.
   *
   * Called from every linked-resource add/update subscription in
   * `getData()`; a single method keeps the retry policy in one
   * place rather than diverging across five sources.
   */
  private onLinkedResourceArrived(): void {
    if (!this.cart || !this.cart.items) { return; }
    let anyBecameReady = false;
    for (const item of this.cart.items) {
      if (item.quantity === 0) { continue; }
      if (item.productionRatesStatus !== 'ERROR' && item.productionRatesStatus !== 'NONE') { continue; }
      if (typeof (item as any).hasCompleteRateInputs === 'function'
          && !(item as any).hasCompleteRateInputs()) { continue; }
      // Inputs became complete after the initial deferral — re-fire
      // the per-item rate fetch. `retryItemPricing` clears the cache
      // slot (a no-op for `NONE`) then fires a fresh HTTP call.
      const itemId = item.getId ? item.getId() : '?';
      console.log(`[cart] item ${itemId} inputs ready — (re)firing rate fetch (was ${item.productionRatesStatus})`);
      this.retryItemPricing(item);
      anyBecameReady = true;
    }
    // If any item just became eligible AND the cart already has an
    // address (which is what originally triggered `Cart.compute()`
    // via `setAddress`), re-run the compute so the deferred
    // shipping-package call inside `PrintNodeOrderOption.compute()`
    // now fires. Cart.compute is a no-op when no address / no
    // non-zero items; safe to call unconditionally when the guard
    // above triggered.
    if (anyBecameReady && this.cart.address && this.cart.address.postalcode) {
      try {
        (this.cart as any).compute();
      } catch (e) {
        console.error('[cart] Cart.compute() after linked-resource arrival failed', e);
      }
    }
  }

  /**
   * Per-line pricing state string. Delegates to the model's
   * {@link Item.getPricingState}; kept as a thin component-side
   * accessor so the template can bind
   * `[ngSwitch]="getItemPriceState(item)"` in a component-scope-only
   * expression without leaking model helpers through the template
   * `let-data` context. Returns `'ZERO'` when the item is undefined
   * or the accessor is missing (older ProductItem/DraftItem instances
   * predate the helper).
   */
  getItemPriceState(item: any): string {
    if (!item) { return 'ZERO'; }
    if (typeof item.getPricingState === 'function') { return item.getPricingState(); }
    // Legacy fallback: derive from status/quantity/cost only.
    if (!item.quantity || item.quantity === 0) { return 'ZERO'; }
    if (item.productionRatesStatus === 'NONE' || item.productionRatesStatus === 'LOADING') { return 'PENDING'; }
    if (item.productionRatesStatus === 'ERROR') { return 'ERROR'; }
    return 'READY';
  }

  /**
   * True when at least one non-zero item is still waiting on its
   * production-rate fetch (`PENDING` state). Drives the summary-bar
   * "Calculating…" spinner and the CHECKOUT-mode "Pending" totals so
   * an in-progress $0.00 running total isn't misread as final.
   */
  get anyItemPricingPending(): boolean {
    if (!this.cart || !this.cart.items) { return false; }
    for (const item of this.cart.items) {
      if (item.quantity > 0 && this.getItemPriceState(item) === 'PENDING') { return true; }
    }
    return false;
  }

  /**
   * True when at least one non-zero item is in a checkout-blocking
   * failure state (`ERROR` or `INFEASIBLE`). Surfaces the red hint bar
   * above the item grid and — via {@link hasItemsBlockingCheckout} —
   * disables the toolbar Checkout button.
   */
  get anyItemPricingErrored(): boolean {
    if (!this.cart || !this.cart.items) { return false; }
    for (const item of this.cart.items) {
      if (item.quantity > 0) {
        const s = this.getItemPriceState(item);
        if (s === 'ERROR' || s === 'INFEASIBLE') { return true; }
      }
    }
    return false;
  }

  /**
   * True when the cart is in a state that prevents advancing to
   * CHECKOUT: any non-zero item is `PENDING`, `ERROR`, or `INFEASIBLE`.
   * Consumed by `cart.component.ts.checkoutDisabled` (via the shared
   * `Cart` model) and by the summary-bar hint. Ready-only items and
   * zero-quantity items don't block.
   */
  get hasItemsBlockingCheckout(): boolean {
    return this.anyItemPricingPending || this.anyItemPricingErrored;
  }

  /**
   * Compute sales tax once shipping has been resolved and a delivery address is in hand.
   * No-op when the cart isn't in a state that warrants the call.
   *
   * Exempt-fast-path (per `docs/architecture/order-placement.md` §3): when the
   * most recent tax response came back zero (exemption certificate, no-tax
   * state, etc.) AND the delivery address fingerprint hasn't changed since
   * that call, production/shipping cost changes alone do not force a
   * recompute. Address change always invalidates (handled in
   * `Cart.setAddress` which clears `salesTaxResult` + signature +
   * fingerprint).
   */
  private maybeComputeSalesTax() {
    if (!this.cart) { return; }
    if (this.cart.cartStage !== 'CHECKOUT') { return; }
    if (this.cart.addressStatus !== 'VALIDATED' && this.cart.addressStatus !== 'MANUAL_OVERRIDE') { return; }
    if (this.cart.shippingStatus !== 'READY') { return; }
    // Also require the picker to have committed a selection to the
    // cart — otherwise `shippingCost` / `packingAndPackagingCost`
    // are still their defaults. Auto-select writes these fields
    // right before it fires `shippingChoiceAutoSelected`, which
    // is the event that calls us in the default-selected case.
    if (!this.cart.selectedOrderOption) { return; }
    // Wait until the SELECTED layout's per-package rates have all
    // landed. The outer `shippingStatus` flips to 'READY' after the
    // first `getPackages` response arrives — long before per-package
    // rate calls resolve — and the earlier whole-tree check
    // (`isShippingFullyComputed`) blocked forever whenever any
    // OTHER carrier's layout had a stuck / errored rate call
    // (`PrintNodeOrderOption.compute`'s non-SUCCESS branch is a no-op,
    // so those packages sit at `'COMPUTING'` permanently). Since sales
    // tax only needs the SELECTED shipping/packing costs, gate on the
    // selected layout only (bug 2026-07-22: auto-selected default
    // never triggered tax when a sibling layout's rate call stalled).
    if (!this.cart.isSelectedShippingFullyComputed()) { return; }

    // Exempt-fast-path: skip the server round-trip if the last result was zero
    // AND the address fingerprint matches.
    const currentFingerprint = this.cart.computeAddressFingerprint(this.cart.address);
    if (this.cart.salesTaxResult
        && this.cart.salesTaxResult.totalTax === 0
        && this.cart.salesTaxAddressFingerprint
        && this.cart.salesTaxAddressFingerprint === currentFingerprint) {
      // Reuse the cached exempt result. taxCost is already 0.
      this.cart.salesTaxStatus = 'READY';
      this.cart.taxCost = 0;
      return;
    }

    this.cart.salesTaxStatus = 'COMPUTING';
    const orgId = Number(this.startupService.selectedRoleId);
    const a = this.cart.address;
    this.salesTaxService.getRate(
      orgId, a.address1 || '', a.city || '', a.state || '', a.postalcode || '', a.country || 'US',
      this.totalCost,
      this.cart.packingAndPackagingCost || 0,
      this.cart.shippingCost || 0,
      0, 0
    ).subscribe(result => {
      if (result) {
        this.cart.salesTaxResult = result;
        this.cart.taxCost = result.totalTax;
        this.cart.salesTaxSignature = result.signature || '';
        this.cart.salesTaxAddressFingerprint = currentFingerprint;
        this.cart.salesTaxStatus = 'READY';
      } else {
        this.cart.salesTaxStatus = 'ERROR';
      }
    });
  }

  /**
   * Idempotent auto-trigger for the legacy `Cart.compute()` shipping
   * pipeline. Called from every code path where "the address just
   * settled" or "we just entered CHECKOUT with a ready address":
   *
   *  - `proceedToCheckout()` — user clicked "Checkout" or the parent
   *    flipped `mode` to CHECKOUT.
   *  - `checkAddressIsComplete()` — user picked / validated / entered
   *    an address; also fires after every field-change event via the
   *    child section's `addressInfoChanged` emitter.
   *
   * Only fires when the cart has (a) non-zero items, (b) a ready
   * address, and (c) no fresh shipping tree already cached for the
   * current inputs. Condition (c) is approximated by
   * `shippingStatus === 'INITIAL' | 'PENDING' | 'ERROR'` OR an empty
   * `orderOptions[]` — either means we don't already have a live
   * per-package rate tree the user can pick from. When `COMPUTING`
   * we're already in flight and re-firing would just cancel and
   * restart, so we skip. When `READY` with a populated tree the user
   * either already has options or the tree is a stale view of a
   * previous cart state that `invalidateDownstream()` will have
   * blanked; either way, don't blindly re-fire.
   *
   * The debounced `scheduleShippingRecompute()` path handles the
   * post-invalidation "quantity changed → re-fire" case; this method
   * covers "address changed → fire (immediately, not debounced)"
   * since address-settled events are one-shot rather than bursty.
   */
  private maybeAutoComputeShipping() {
    if (!this.cart) { return; }
    if (this.cart.addressStatus !== 'VALIDATED' && this.cart.addressStatus !== 'MANUAL_OVERRIDE') { return; }
    const nonZero = (this.cart.items || []).filter(i => i && i.quantity > 0);
    if (nonZero.length === 0) { return; }
    const status = this.cart.shippingStatus;
    const hasFreshTree = (this.cart.orderOptions && this.cart.orderOptions.length > 0)
        && (status === 'READY' || status === 'COMPUTING')
        // ...and computed for the address the cart carries RIGHT NOW. The
        // address form mutates `cart.address` in place and
        // `applyValidatedAddress()` rewrites street/city/ZIP without going
        // through `setAddress()`, so a tree built while the user was still
        // typing (or before validation corrected the ZIP) looks "fresh" by
        // status alone while actually quoting a different destination —
        // and the facility choice itself depends on the ZIP.
        && !this.cart.isShippingStaleForAddress();
    if (hasFreshTree) { return; }
    try {
      this.cart.compute();
    } catch (e) {
      console.error('maybeAutoComputeShipping: cart.compute failed', e);
    }
  }

  /**
   * When `environment.useNewCartShippingFlow` is on AND the address is
   * ready AND the cart has non-zero items, run the new
   * getFacilities → rateShipping pipeline. Runs alongside (not instead
   * of) the legacy `Cart.compute()` flow so the user-visible UI continues
   * to work; the new flow drives `cart.facilityCandidates` /
   * `cart.rateShippingByFacility` for downstream consumers and for the
   * preferred-shipping auto-select logic.
   *
   * Idempotent: if the cart already has fresh `facilityCandidates` and
   * `rateShippingByFacility` for the current address+items combination,
   * it does nothing. `Cart.setAddress` and `Cart.invalidateDownstream`
   * wipe those fields whenever inputs change.
   */
  private maybeRunNewShippingFlow() {
    if (!environment.useNewCartShippingFlow) { return; }
    if (!this.cart) { return; }
    if (this.cart.addressStatus !== 'VALIDATED' && this.cart.addressStatus !== 'MANUAL_OVERRIDE') { return; }
    const nonZero = (this.cart.items || []).filter(i => i && i.quantity > 0);
    if (nonZero.length === 0) { return; }
    // Already have facility candidates + rates for this cart state? Skip.
    if (this.cart.facilityCandidates && this.cart.facilityCandidates.length > 0
        && this.cart.rateShippingByFacility
        && Object.keys(this.cart.rateShippingByFacility).length > 0) {
      this.maybeAutoSelectPreferredShipping();
      return;
    }
    this.orderService.getFacilitiesForCart(this.cart).subscribe(
      () => {
        if (!this.cart.facilityCandidates || this.cart.facilityCandidates.length === 0) {
          // No eligible facility — leave the legacy flow to surface its own error.
          return;
        }
        this.orderService.rateShippingForCart(this.cart).subscribe(
          () => this.maybeAutoSelectPreferredShipping(),
          (err: any) => console.error('[cart] new shipping flow: rateShipping failed', err)
        );
      },
      (err: any) => console.error('[cart] new shipping flow: getFacilities failed', err)
    );
  }

  /**
   * When the cart's Organization has `preferredCarrier` +
   * `preferredServiceLevel` set AND the new shipping flow has populated
   * `cart.rateShippingByFacility`, search the rates of the first feasible
   * facility for a matching `(carrier, serviceLevel)` row. If found,
   * capture the row's signature and packageSetId into the cart so they
   * can be echoed in the place-order envelope.
   *
   * No-op when no preference is set, when no matching rate exists for
   * any facility, or when the new shipping flow is off.
   */
  private maybeAutoSelectPreferredShipping() {
    if (!this.cart || !this.cart.organization) { return; }
    const prefCarrier = (this.cart.organization.preferredCarrier || '').trim();
    const prefService = (this.cart.organization.preferredServiceLevel || '').trim();
    if (!prefCarrier || !prefService) { return; }
    if (!this.cart.rateShippingByFacility) { return; }
    for (const facilityId of Object.keys(this.cart.rateShippingByFacility)) {
      const perFac = this.cart.rateShippingByFacility[Number(facilityId)];
      if (!perFac || !perFac.rates) { continue; }
      const match = perFac.rates.find(r => r.carrier === prefCarrier && r.serviceLevel === prefService);
      if (match) {
        this.cart.selectedCarrier = match.carrier;
        this.cart.selectedServiceLevel = match.serviceLevel;
        this.cart.selectedShippingRateSignature = match.signature || '';
        this.cart.selectedPackageSetId = match.packageSetId || '';
        // `match.totalCost` is the carrier's per-package rate sum —
        // pure shipping only, no packing markup. Packing lives in the
        // backing `packageSets[<matchedSet>].packages[].packingCost`;
        // sum it here so the order summary's "Packing and Packaging"
        // row shows a real value rather than $0 and the tax
        // service can receive them as separate arguments (some
        // jurisdictions tax packing/handling differently from
        // shipping — see 2026-07-09 pass 3 fix, mirrored in
        // `Cart.setSelectedShippingChoice` for the unified-picker path).
        const pkgSet = (perFac.packageSets || []).find(ps => ps.packageSetId === match.packageSetId);
        let packingSum = 0;
        if (pkgSet && pkgSet.packages) {
          for (const p of pkgSet.packages) {
            packingSum += (p.packingCost || 0);
          }
        }
        this.cart.shippingCost = match.totalCost;
        this.cart.packingAndPackagingCost = packingSum;
        this.cart.shippingStatus = 'READY';
        console.log(`[cart] auto-selected preferred shipping ${prefCarrier}/${prefService} at facility ${facilityId} for $${match.totalCost.toFixed(2)} shipping + $${packingSum.toFixed(2)} packing`);
        return;
      }
    }
    // No match — leave the user to pick manually.
    console.log(`[cart] preferred shipping ${prefCarrier}/${prefService} not available for any candidate facility`);
  }

  // ──────────────────────────────────────────────────────────────────────
  // Section-state helpers (icon + css class) were moved to the
  // CartAddressSectionComponent / CartShippingSectionComponent /
  // CartPaymentSectionComponent extractions on 2026-06-24. The parent
  // no longer renders the per-section headers directly.
  // ──────────────────────────────────────────────────────────────────────

  /** True when sales tax has been successfully computed. Tolerates legacy 'COMPLETE'. */
  get salesTaxReady(): boolean {
    if (!this.cart) { return false; }
    return this.cart.salesTaxStatus === 'READY' || (this.cart.salesTaxStatus as string) === 'COMPLETE';
  }

  // ──────────────────────────────────────────────────────────────────────
  // Address-book save prompt and `validateCartAddress()` moved to
  // CartAddressSectionComponent (2026-06-24).
  // ──────────────────────────────────────────────────────────────────────

  getPlaceOrderButtonClass(): string {
    if ( this.placingOrderStatus === 'Success') {
      return 'processing btn-success';
    }
    if ( this.placingOrderStatus === 'PleaseWait') {
      return 'processing btn-light';
    }
    if ( this.placingOrderStatus !== '') {
      return 'processingProblem btn-danger';
    }
    if ( this.addressIsComplete && this.paymentVerified && this.costsAndShippingVerified ) {
      return 'ready btn-primary';
    } else {
      return 'notReady btn-warning';
    }
  }

  makePurchase() {
    // Stub retained because cart-page.component.html still binds the
    // "Place Order" button to `makePurchase()` for the non-invoiced path.
    // The real submission flow goes through `preparePaymentThenPlaceOrder`
    // (invoke directly when wiring the new place-order endpoint, client task F).
    // Until then, route through the same path the Invoiced button uses so
    // existing behavior is unchanged.
    if (!this.cart) { return; }
    this.placingOrderStatus = 'PleaseWait';
    this.placingOrderMessage = '';
    this.preparePaymentThenPlaceOrder();
  }

  ngOnDestroy(): void {
    // Cancel any debounced shipping recompute so it can't fire against
    // a torn-down view (see `scheduleShippingRecompute`).
    if (this.shippingRecomputeTimer) {
      clearTimeout(this.shippingRecomputeTimer);
      this.shippingRecomputeTimer = null;
    }
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

//////////// CREATING Order, OrderDeliverTo, OrderItems, PrintNodeOrder and PrintNodeOrderItems
  // Delegates to OrderService.placeOrder which orchestrates the multi-step
  // creation in a single observable chain.
  placeInvoicedOrder() {
    if (!this.cart) { return; }
    this.placingOrderStatus = 'PleaseWait';
    this.placingOrderMessage = '';
    this.preparePaymentThenPlaceOrder();
  }

  /**
   * Resolve the payment side of the cart before placing the order:
   *   - Invoiced payment: nothing to tokenize; place the order directly.
   *   - Existing ChargeProfile selected and not expired: place the order directly.
   *   - Newly entered credit card AND user opted to save: tokenize via
   *     ChargeProfileService.createChargeProfile, attach the resulting profile
   *     to the cart, then place the order.
   *   - Newly entered credit card without save: still send raw fields to the
   *     server inline at order time (server will tokenize for the single charge).
   */
  private preparePaymentThenPlaceOrder() {
    // Path 1: invoiced — nothing to do.
    if (this.invoice && this.cart.invoiceable) {
      this.submitOrder();
      return;
    }

    // Path 2: existing profile selected and still valid.
    if (this.cart.chargeProfile && this.cart.chargeProfile.ID > 0) {
      if (this.cart.chargeProfile.isNotExpired && !this.cart.chargeProfile.isNotExpired()) {
        this.placingOrderStatus = 'Error';
        this.placingOrderMessage = 'Selected card has expired. Please choose another card or enter a new one.';
        return;
      }
      this.submitOrder();
      return;
    }

    // Path 3 / 4: a credit-card form was filled in.
    if (this.enterCreditCard && this.cart.cardNumberWithSpaces) {
      if (this.savePaymentInfoAsChargeProfile) {
        this.tokenizeAndSaveCard();
      } else {
        // One-time charge — server will tokenize on the order-create call.
        this.submitOrder();
      }
      return;
    }

    this.placingOrderStatus = 'Error';
    this.placingOrderMessage = 'No payment method selected.';
  }

  private tokenizeAndSaveCard() {
    const cardNumber = (this.cart.cardNumberWithSpaces || '').split(' ').join('');
    const expMonth = Number((this.cart.expMonthYear || '').substring(0, 2));
    let expYear = Number((this.cart.expMonthYear || '').substring(3));
    if (expYear < 100) { expYear += 2000; }

    const useShipping = this.billingAddressTheSameAsShipping;
    this.chargeProfileService.createChargeProfile({
      name: this.savePaymentInfoAsChargeProfileName || `${this.cart.firstNamePayment} ${this.cart.lastNamePayment}`.trim(),
      firstName: this.cart.firstNamePayment,
      lastName: this.cart.lastNamePayment,
      cardNumber: cardNumber,
      expMonth: expMonth,
      expYear: expYear,
      csc: this.cart.csc,
      address1: useShipping ? this.cart.address.address1 : this.cart.address1Payment,
      address2: useShipping ? this.cart.address.address2 : this.cart.address2Payment,
      city: useShipping ? this.cart.address.city : this.cart.cityPayment,
      state: useShipping ? this.cart.address.state : this.cart.statePayment,
      postalCode: useShipping ? this.cart.address.postalcode : this.cart.postalCodePayment,
      country: useShipping ? this.cart.address.country : this.cart.countryPayment,
      setAsDefault: false
    }).subscribe(
      profile => {
        if (profile && profile.ID > 0) {
          this.cart.setChargeProfile(profile);
          // Wipe raw card fields once we have the token.
          this.cart.cardNumber = '';
          this.cart.cardNumberWithSpaces = '';
          this.cart.csc = '';
          this.submitOrder();
        } else {
          this.placingOrderStatus = 'Error';
          this.placingOrderMessage = 'Could not save the credit card. Please verify the card details.';
        }
      },
      err => {
        this.placingOrderStatus = 'Error';
        this.placingOrderMessage = 'Card tokenization failed. ' + (err && err.message ? err.message : '');
        console.error('createChargeProfile failed', err);
      }
    );
  }

  private submitOrder() {
    if (environment.useNewCartPlaceOrder) {
      this.submitOrderViaNewEndpoint();
      return;
    }
    this.orderService.placeOrder(this.cart).subscribe(
      createdOrder => {
        if (createdOrder) {
          this.placingOrderStatus = 'Success';
          this.placingOrderMessage = '';
          this.cart.clear();
        } else {
          this.placingOrderStatus = 'Error';
          this.placingOrderMessage = 'Order was not created.';
        }
      },
      err => {
        this.placingOrderStatus = 'Error';
        // PF-13: `placeOrder` no longer flattens an OrderDeliverTo rejection
        // into a generic "Failed to create OrderDeliverTo" -- the server's own
        // sentence survives the chain and is what the customer sees.
        this.placingOrderMessage = describeServerError(err,
          LanguagePipe.get('The order could not be created.'));
        this.toastError(this.placingOrderMessage);
        console.error('placeOrder failed', err);
      }
    );
  }

  /**
   * Submit the cart via the new `order/placeorder` endpoint (S-6).
   * Currently uses the dry-run path because the server has not yet wired
   * payment processing or full signature-scope verification — even
   * though the S3-backed envelope queue + creator Lambda persistence path
   * is now live (per inbox 2026-06-24-placeorder-s3-persistence-live.md).
   * Switching this to dryRun=false today would persist orders without
   * charging, so we hold until the server's payment-processing follow-up
   * lands.
   *
   * Gated behind `environment.useNewCartPlaceOrder`; default off
   * everywhere. As the server lights up payment + full signature
   * verification, this method will flip from dryRun=true to dryRun=false
   * and wire the order-watcher subscription that picks up Add
   * notifications as objects are created.
   */
  /**
   * Submit the cart via the single-round-trip `order/placeorder`
   * endpoint (S-6). The client builds a full envelope containing
   * `order`, `orderDeliverTos[]` (with `orderItems`),
   * `printNodeOrders[]` (each with its own `printNodeOrderItems`
   * + `packages[]` + `orderItemToPackages[]`), `payment`, and
   * `signatures`. See `docs/architecture/order-placement.md` §4 and
   * `docs/contracts/shared/PlaceOrder.md`.
   *
   * Server processes the envelope in three phases: validate +
   * signature-verify → payment (if any) → S3-queue + creator-Lambda
   * handoff. On `ACCEPTED` the client:
   *
   *   1. Shows a "your order is being placed" message via
   *      `AlertService`.
   *   2. Clears the cart (`cart.clear()` also flips
   *      `cartStage = 'CART'` so the user is back at stage 1 —
   *      pick titles + quantities — which is the requested UX).
   *   3. Emits `modeChange('CART')` so the parent view swaps back.
   *   4. Relies on the normal object-notification stream to surface
   *      the freshly-created `Order` / `OrderDeliverTo` / `PrintNodeOrder`
   *      rows once the creator-Lambda finishes. No polling here.
   *
   * Uses `placeOrderViaEnvelope` unconditionally. There is no dry-run
   * mode and no flag: every place-order really charges and really
   * persists, in every environment.
   */
  private submitOrderViaNewEndpoint() {
    this.placingOrderStatus = 'PleaseWait';
    this.placingOrderMessage = '';

    this.orderService.placeOrderViaEnvelope(this.cart).subscribe(
      (resp: any) => {
        if (resp && resp.status === 'ACCEPTED') {
          console.log('[cart] placeOrder accepted', { resp });
          // Server has queued the envelope; DB rows will arrive
          // asynchronously via the notification stream.
          this.placingOrderStatus = 'Success';
          this.placingOrderMessage = LanguagePipe.get(
            'Your order is being placed. You can watch the Orders page for updates.'
          );
          this.toastSuccess(this.placingOrderMessage);
          // Clear cart and jump back to stage 1 (title/qty selection).
          this.cart.clear();
          this.cart.cartStage = 'CART';
          this.modeChange.emit('CART');
          // Cart.clear() zeroed all per-item quantities + costs and
          // the cart-level cost/tax fields, but the *component*
          // still holds its own copies of the summary totals and
          // the per-section readiness flags. Force a recompute so
          // the CART view lands with fresh $0.00 / empty state
          // instead of the last-submitted order's numbers, and
          // clear the placing-order status field so the "please
          // wait" indicator doesn't linger. verifyCostsAndShipping
          // internally sets `cart.itemsStatus = 'EMPTY'` when
          // totalQuantity is 0, which cascades through the
          // readiness getters.
          this.totalCost = 0;
          this.totalWeight = 0;
          this.totalQuantity = 0;
          this.itemsReady = false;
          this.addressReady = false;
          this.shippingReady = false;
          this.paymentReady = false;
          this.placingOrderStatus = '';
          this.placingOrderMessage = '';
          this.verifyCostsAndShipping();
        } else if (resp && resp.status === 'VALIDATION_FAILED') {
          this.placingOrderStatus = 'Error';
          this.placingOrderMessage = 'Order failed validation: '
            + (resp.errors ? resp.errors.map((e: any) => `${e.path}: ${e.message}`).join('; ') : '(no detail)');
          this.toastError(this.placingOrderMessage);
        } else if (resp && resp.status === 'SIGNATURE_MISMATCH') {
          this.placingOrderStatus = 'Error';
          this.placingOrderMessage = LanguagePipe.get('Prices changed - please review and resubmit.');
          this.toastError(this.placingOrderMessage);
          console.warn('[cart] placeOrder signature mismatch', resp);
        } else if (resp && resp.status === 'DECLINED') {
          this.placingOrderStatus = 'Error';
          this.placingOrderMessage = 'Payment was declined: ' + ((resp as any).reason || '(no detail)');
          this.toastError(this.placingOrderMessage);
        } else if (resp && resp.status === 'PAYMENT_FAILED') {
          this.placingOrderStatus = 'Error';
          this.placingOrderMessage = 'Payment failed: ' + ((resp as any).reason || '(no detail)');
          this.toastError(this.placingOrderMessage);
        } else {
          this.placingOrderStatus = 'Error';
          this.placingOrderMessage = 'Unexpected server response: ' + JSON.stringify(resp);
          this.toastError(this.placingOrderMessage);
        }
      },
      (err: any) => {
        this.placingOrderStatus = 'Error';
        this.placingOrderMessage = 'Error submitting the order. ' + (err && err.message ? err.message : '');
        this.toastError(this.placingOrderMessage);
        console.error('placeOrder (new endpoint) failed', err);
      }
    );
  }

  makeSimpleOrderFromCart(): SimpleOrder {
    const simpleOrder = new SimpleOrder();
    simpleOrder.firstName = this.cart.address.firstname;
    simpleOrder.lastName = this.cart.address.lastname;
    simpleOrder.company = this.cart.address.company;
    simpleOrder.address1 = this.cart.address.address1;
    simpleOrder.address2 = this.cart.address.address2;
    simpleOrder.city = this.cart.address.city;
    simpleOrder.region = this.cart.address.state;
    simpleOrder.country = this.cart.address.country;
    simpleOrder.postalCode = this.cart.address.postalcode;
    simpleOrder.email = this.cart.address.email;
    simpleOrder.inventoryType = this.cart.inventoryType;
    simpleOrder.shippingInfo = this.cart.shippingMethod;
    simpleOrder.submissionMethod = '';
    simpleOrder.telephone = this.cart.phone;
    // simpleOrder.type = ??

    for ( const cartItem of this.cart.items ) {
      const item = new SimpleItem();
      item.setQuantity(cartItem.quantity);
      item.setId(cartItem.getId());
      if ( item.quantity > 0 ) {
        item.setQuantity(cartItem.quantity);
        simpleOrder.addItem(item);
      }
    }
    return simpleOrder;
  }

  createOrderDeliverTo(simpleOrder: SimpleOrder, order: Order) {
    const orderDeliverTo = new OrderDeliverTo(undefined);
    orderDeliverTo.inventoryType = simpleOrder.inventoryType;
    orderDeliverTo.order_ID = order.ID;

    orderDeliverTo.title = simpleOrder.title ? simpleOrder.title.toUpperCase() : '';
    orderDeliverTo.firstname = simpleOrder.firstName ? simpleOrder.firstName.toUpperCase() : '';
    orderDeliverTo.lastname = simpleOrder.lastName ? simpleOrder.lastName.toUpperCase() : '';
    orderDeliverTo.company = simpleOrder.company ? simpleOrder.company.toUpperCase() : '';
    orderDeliverTo.address1 = simpleOrder.address1 ? simpleOrder.address1 : '';
    orderDeliverTo.address2 = simpleOrder.address2 ? simpleOrder.address2 : '';
    orderDeliverTo.city = simpleOrder.city ? simpleOrder.city : '';
    orderDeliverTo.state = simpleOrder.region ? simpleOrder.region : '';
    orderDeliverTo.postalcode = simpleOrder.postalCode ? simpleOrder.postalCode : '';
    orderDeliverTo.country = simpleOrder.country ? simpleOrder.country : '';

    orderDeliverTo.addressValidationStatus = 'VALID';

    const carrierService = 'USPS_PRIORITY_FLAT';
    let shippingInfoParts = simpleOrder.shippingInfo.split('_');
    if ( !shippingInfoParts || shippingInfoParts.length < 2 ) {
      shippingInfoParts = carrierService.split('_');
    }
    if ( shippingInfoParts && shippingInfoParts.length >= 2) {
      orderDeliverTo.carrier = shippingInfoParts[0];
      orderDeliverTo.servicelevel = shippingInfoParts[1];
      if ( shippingInfoParts && shippingInfoParts.length >= 3) {
        orderDeliverTo.servicelevel += "_" + shippingInfoParts[2];
      }
    }
    // orderId;
    // type;
    // ignored: boolean;
    // confirmed: boolean;

    for ( const item of simpleOrder.items) {
        let orderItem: OrderItem;
        for ( const alreadyCreatedOrderItem of orderDeliverTo.orderItems ) {
          if ( alreadyCreatedOrderItem.product_ID === Number(item.id) ) {
            orderItem = alreadyCreatedOrderItem;
          }
        }
        if ( orderItem ) {
          orderItem.quantity += item.quantity;
        } else {
          orderItem = new OrderItem(undefined);
          orderItem.product_ID = Number(item.id);
          orderItem.quantity = item.quantity;
          orderDeliverTo.orderItems.push(orderItem);
      }
    }

    // PF-13 -- this is the exact site that cleared a customer's cart on a
    // FAILED order.
    //
    // `POST /orderDeliverTo` used to answer 201 with an error body for every
    // validation failure. `OrderDeliverToService` mapped that body through
    // `new OrderDeliverTo(...)`, which is always truthy, so `if (created)`
    // took the success branch and `cart.clear()` ran on an order the server
    // had refused. The customer lost their basket and got no order.
    //
    // The server returns real status codes now, and the service reports
    // failure by ERRORING rather than emitting `undefined`, so:
    //   - the cart is only cleared on the success path, which now genuinely
    //     means "the server created it";
    //   - the failure path has an error handler that says so out loud. It is
    //     the server's own sentence where there is one -- it names the
    //     product or quantity that was rejected, which is what lets the
    //     customer fix it.
    this.orderDeliverToService.addOrderDeliverTo(orderDeliverTo).subscribe(
      createdOrderDeliverTo => {
        this.createdOrderDeliverTo = createdOrderDeliverTo;
        this.placingOrderStatus = 'Success';
        this.placingOrderMessage = '';
        console.log(`Success making OrderDeliverTo ${createdOrderDeliverTo.ID}`);
        this.cart.clear();
      },
      error => {
        this.createdOrderDeliverTo = undefined;
        this.placingOrderStatus = 'Error';
        this.placingOrderMessage = describeServerError(error,
          LanguagePipe.get('The order could not be created.'));
        this.toastError(this.placingOrderMessage);
        console.error('addOrderDeliverTo failed', error);
      }
    );
  }

  // async createPrintNodeOrder(simpleOrder: SimpleOrder, orderDeliverTo: OrderDeliverTo) {
  //   const printNodeOrder = new PrintNodeOrder(undefined);
  //   printNodeOrder.inventoryType = orderDeliverTo.inventoryType;
  //   printNodeOrder.org_ID = Number(this.startupService.selectedRoleId);
  //   printNodeOrder.order_ID = orderDeliverTo.order_ID;
  //   printNodeOrder.printnode_ID = 1;
  //   printNodeOrder.orderdeliverto_ID = orderDeliverTo.ID;
  //   printNodeOrder.address1 = orderDeliverTo.address1;
  //   printNodeOrder.address2 = orderDeliverTo.address2;
  //   printNodeOrder.city = orderDeliverTo.city;
  //   printNodeOrder.state = orderDeliverTo.state;
  //   printNodeOrder.postalcode = orderDeliverTo.postalcode;
  //   printNodeOrder.country = orderDeliverTo.country;
  //   printNodeOrder.title = orderDeliverTo.title;
  //   printNodeOrder.firstname = orderDeliverTo.firstname;
  //   printNodeOrder.lastname = orderDeliverTo.lastname;
  //   printNodeOrder.carrier = orderDeliverTo.carrier;
  //   printNodeOrder.servicelevel = orderDeliverTo.servicelevel;
  //   const dueDate = new Date();
  //   dueDate.setDate(dueDate.getDate() + 1);
  //   printNodeOrder.dueDate = dueDate;
  //
  //   printNodeOrder.printNodeOrderItems = [];
  //   for ( const orderItem of orderDeliverTo.orderItems ) {
  //     const printNodeOrderItem = new PrintNodeOrderItem(undefined, printNodeOrder);
  //     printNodeOrderItem.printNodeOrder = undefined;
  //     printNodeOrderItem.book_ID = this.getBookIdFromProductId(orderItem.product_ID);
  //     printNodeOrderItem.orderitem_ID = orderItem.ID;
  //     printNodeOrderItem.quantity = orderItem.quantity;
  //     printNodeOrder.printNodeOrderItems.push(printNodeOrderItem);
  //   }
  //   // Move this part to createPrintNodeOrder in the same patter as Validate and CreateOrderDeliverTo
  //   this.printNodeOrderService.addItem(printNodeOrder).subscribe( createdPrintNodeOrder => {
  //     if ( createdPrintNodeOrder ) {
  //       this.printNodeOrderCreated = createdPrintNodeOrder;
  //       console.log(`Success making PrintNodeOrder`);
  //     } else {
  //       this.printNodeOrderCreated = undefined;
  //       console.log(`Error making PrintNodeOrder`);
  //     }
  //   });
  // }

  // getBookIdFromProductId(productId: number): number {
  //   for ( const product of this.products) {
  //     if ( product.ID === productId) {
  //       return product.book_ID;
  //     }
  //   }
  //   return 0;
  // }

  isInstanceOf(obj: any, className: any): boolean {
    return obj && obj.constructor.name === className;
  }

  /**
   * Return the `Project.folder` path for the cart item, or an
   * empty string if it can't be resolved for any reason. Used to
   * show where the Book or Draft lives in the operator's folder
   * tree next to the item's date on the cart line.
   *
   * Hardened against every layer being missing so it can be
   * called safely from the template on every change-detection
   * tick without crashing the render:
   *
   * - `item` itself may be null / not-yet-populated.
   * - `item.getBase` may not exist on some item variants.
   * - `item.getBase()` may throw (defensive try/catch below).
   * - For a `ProductItem`: `base.book`, `base.book.project`, or
   *   `base.book.project.folder` may each be missing.
   * - For a `DraftItem`: `base.project` or `base.project.folder`
   *   may be missing.
   * - `folder` may be a non-string (defensive `String(...)`).
   *
   * Any resolution failure returns an empty string so the
   * `*ngIf="getFolder(item)"` gate in the template hides the
   * chip cleanly — matching the pre-folder rendering exactly.
   */
  getFolder(item: any): string {
    try {
      if (!item || typeof item.getBase !== 'function') { return ''; }
      const base = item.getBase();
      if (!base) { return ''; }
      let folder: any = '';
      if (this.isInstanceOf(item, 'ProductItem')) {
        const book = base.book;
        if (book && book.project && book.project.folder) {
          folder = book.project.folder;
        }
      } else if (this.isInstanceOf(item, 'DraftItem')) {
        if (base.project && base.project.folder) {
          folder = base.project.folder;
        }
      }
      if (!folder) { return ''; }
      return typeof folder === 'string' ? folder : String(folder);
    } catch (e) {
      // Never let a folder-resolution failure break cart rendering.
      console.error('CartPageComponent.getFolder failed', e);
      return '';
    }
  }

  get totalBookCount(): number {
    if (!this.cart || !this.cart.items) { return 0; }
    return this.cart.items.filter(item => this.isInstanceOf(item, 'ProductItem') && item.quantity > 0).length;
  }

  get totalDraftCount(): number {
    if (!this.cart || !this.cart.items) { return 0; }
    return this.cart.items.filter(item => this.isInstanceOf(item, 'DraftItem') && item.quantity > 0).length;
  }

  get totalBookQuantity(): number {
    if (!this.cart || !this.cart.items) { return 0; }
    return this.cart.items.filter(item => this.isInstanceOf(item, 'ProductItem')).reduce((sum, item) => sum + item.quantity, 0);
  }

  get totalDraftQuantity(): number {
    if (!this.cart || !this.cart.items) { return 0; }
    return this.cart.items.filter(item => this.isInstanceOf(item, 'DraftItem')).reduce((sum, item) => sum + item.quantity, 0);
  }

  incrementQuantity(item: any) {
    if (item.quantity < 2000) {
      const wasZero = item.quantity === 0;
      item.previousQuantity = item.quantity;
      item.quantity = item.quantity + 1;
      this.onItemQuantityChanged(item, wasZero);
    }
  }

  decrementQuantity(item: any) {
    if (item.quantity > 0) {
      const wasZero = false;
      item.previousQuantity = item.quantity;
      item.quantity = item.quantity - 1;
      this.onItemQuantityChanged(item, wasZero);
    }
  }

  onQuantityInput(item: any, event: Event) {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    const wasZero = item.quantity === 0;
    item.previousQuantity = item.quantity;
    if (!isNaN(value) && value >= 0 && value <= 2000) {
      item.quantity = value;
    } else if ((event.target as HTMLInputElement).value === '') {
      item.quantity = 0;
    }
    this.onItemQuantityChanged(item, wasZero);
  }

  /**
   * Common post-quantity-change processing:
   *  - Fetch production rates lazily on a 0→>0 transition.
   *  - Recompute item cost (safe when rates are in flight; will recompute again on arrival).
   *  - Invalidate downstream computations (facility/packages/shipping/tax)
   *    UNCONDITIONALLY — quantity changes made in CART mode also
   *    invalidate the previously-computed shipping tree, otherwise a
   *    user who "back to cart"-ed, changed a quantity, and returned
   *    to CHECKOUT would see the old cart's shipping options against
   *    the new item set (bug reported 2026-07-09). `proceedToCheckout`
   *    picks the invalidated state up on re-entry via
   *    `maybeAutoComputeShipping()` and re-fires `cart.compute()`.
   *  - Debounce-schedule `cart.compute()` so shipping rates re-fetch
   *    without needing the user to hit the "Compute order costs" button
   *    (bug reported 2026-07-08). Only scheduled when we're actually
   *    in CHECKOUT — no point spinning up a shipping-package fetch
   *    while the user is still adjusting item quantities in CART mode
   *    with the section not even visible.
   *  - Refresh totals and summary status.
   *  - Defensively re-check every other non-zero item's rate status
   *    so any item that (via a race, error, or cache reset) lost its
   *    `READY` status without a re-fetch also gets one now — avoids
   *    the user having to nudge each item individually to un-stick
   *    a stuck price.
   */
  private onItemQuantityChanged(item: any, wasZero: boolean) {
    if (wasZero && item.quantity > 0 && item.productionRatesStatus !== 'READY' && item.productionRatesStatus !== 'LOADING') {
      this.fetchProductionRatesForItem(item);
    }
    try { item.quantityChanged(); } catch (e) { console.error('Error in quantityChanged', e); }
    if (this.cart) {
      // Invalidate regardless of cartStage — the cached shipping tree
      // is stale the moment any item's quantity moves, and letting it
      // linger into a future CHECKOUT pass shows the user options
      // computed against the wrong item set.
      this.cart.invalidateDownstream();
      // Flip `shippingReady` off in the parent's state so the payment
      // section can't be opened over a re-computing shipping section
      // and the summary bar doesn't show a stale rate line.
      this.shippingReady = false;
      if (this.cart.cartStage === 'CHECKOUT') {
        // `invalidateDownstream` sets `shippingStatus` to PENDING (which
        // paints the "Getting shipping options" spinner), but does not
        // itself trigger a network call. Historically the only way to
        // fire a fresh `packageandshippingcomputation/packages` request
        // was to click the "Compute order costs" button in the shipping
        // section — quantity changes silently left the UI stuck showing
        // a spinner over stale-or-blank rate rows. Schedule a debounced
        // `cart.compute()` here so quantity edits self-heal without user
        // intervention. The address section separately calls
        // `Cart.setAddress()` which auto-computes, so we only need to
        // cover the item-change path.
        this.scheduleShippingRecompute();
      }
    }
    this.verifyCostsAndShipping();
    // Defensive re-check across the rest of the cart: any other non-
    // zero item whose rate fetch was never fired (or errored + was
    // manually flipped to NONE) picks up a request now. Cheap when
    // everything's already READY / LOADING — the guard in
    // `ensureProductionRatesForNonZeroItems` skips those.
    this.ensureProductionRatesForNonZeroItems();
  }

  /**
   * Debounce window (ms) used by {@link scheduleShippingRecompute} to
   * coalesce a rapid sequence of quantity edits (typing "12" fires
   * two `onQuantityInput` events; increment/decrement clicks can also
   * come in bursts) into a single `cart.compute()` invocation. Long
   * enough that a normal two- or three-digit quantity finishes typing
   * before the timer fires, short enough that the user perceives the
   * spinner as reacting immediately to their last edit.
   */
  private static readonly SHIPPING_RECOMPUTE_DEBOUNCE_MS = 400;

  /** Handle for the pending `cart.compute()` scheduled by {@link scheduleShippingRecompute}. */
  private shippingRecomputeTimer: any = null;

  /**
   * Schedule a debounced `cart.compute()` so quantity / item-set
   * changes trigger a shipping-rate re-fetch without the user having
   * to click the "Compute order costs" button. Safe to call
   * repeatedly — each call resets the debounce window. Cleared in
   * `ngOnDestroy` so a pending recompute never fires against a
   * torn-down view. See the note in `onItemQuantityChanged`.
   */
  private scheduleShippingRecompute() {
    if (!this.cart) { return; }
    if (this.shippingRecomputeTimer) {
      clearTimeout(this.shippingRecomputeTimer);
    }
    this.shippingRecomputeTimer = setTimeout(() => {
      this.shippingRecomputeTimer = null;
      // Guard: the cart may have been torn down or the user may have
      // clicked "Back" to CART mode while the timer was pending.
      if (!this.cart || this.cart.cartStage !== 'CHECKOUT') { return; }
      try {
        this.cart.compute();
      } catch (e) {
        console.error('scheduled cart.compute failed', e);
      }
    }, CartPageComponent.SHIPPING_RECOMPUTE_DEBOUNCE_MS);
  }

  itemMatchesSearch(item: any): boolean {
    if (!this.searchString || this.searchString.length === 0) { return true; }
    const search = this.searchString.toLowerCase();
    const title = item.getTitle ? item.getTitle() : '';
    return title.toLowerCase().includes(search);
  }

  itemMatchesFilters(item: any): boolean {
    return (this.showNonZero || item.quantity > 0)
      && (this.showProducts || !this.isInstanceOf(item, 'ProductItem'))
      && (this.showDrafts || !this.isInstanceOf(item, 'DraftItem'))
      && this.itemMatchesSearch(item);
  }

  // ──────────────────────────────────────────────────────────────────────
  // (removed 2026-07-23) Diagnostic test buttons — the "Cart diagnostics
  // (internal)" strip and its per-endpoint handlers
  // (testFetchProductionRates / testGetFacilities / testRateShipping /
  // testSalesTax / testPlaceOrderDryRun) plus the `showCartTestButtons`
  // gate were removed. All of those endpoints are now exercised by the
  // real checkout flow, so the console-logging dev-only strip is no
  // longer needed. The underlying service methods
  // (OrderService.getProductionRatesForItem / getFacilitiesForCart /
  // rateShippingForCart / placeOrderDryRun and SalesTaxService.getRate)
  // are still called from the production code paths above.
  // ──────────────────────────────────────────────────────────────────────
}

